import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { ClaimCard } from '@/components/ui/shared/ClaimCard';

/**
 * CompanyOverviewClaim - Claims statistics widget for Company Overview page
 * Displays claim count items with individual loaders matching transaction page design
 */
function CompanyOverviewClaim({ data }) {
    const [claimData, setClaimData] = useState([]);
    const [loading, setLoading] = useState(!!(data?.RequestJson && data?.url));
    const [error, setError] = useState(null);
    
    const abortControllerRef = useRef(null);
    const prevRequestJsonRef = useRef(null);

    const handleResponse = useCallback(async (response) => {
        try {
            const text = await response.text();
            const data = text && JSON.parse(text);
            return data;
        } catch {
            return [];
        }
    }, []);

    const fetchDataLocally = useCallback(async () => {
        if (!data?.RequestJson || !data?.url) return;
        
        // Cancel any previous pending request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;
        
        setLoading(true);
        setError(null);
        
        try {
            const requestOptions = {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json', 
                    'requestHeader': data.RequestJson1, 
                    'X-Browser-Url': window.location.href 
                },
                body: data.RequestJson,
                signal: controller.signal,
            };
            
            const response = await fetch(data.url, requestOptions);
            const apidata = await handleResponse(response);
            
            if (controller.signal.aborted) return;
            
            if (apidata?.[0]?.legend) {
                setClaimData(apidata[0].legend);
            }
            setLoading(false);
        } catch (err) {
            if (err.name === 'AbortError') return;

            setError('Error loading data');
            setLoading(false);
        }
    }, [data?.url, data?.RequestJson, data?.RequestJson1, handleResponse]);

    // Fetch on mount and when RequestJson changes
    useEffect(() => {
        if (data?.type === "Claim" && data?.RequestJson && data?.url &&
            data.RequestJson !== prevRequestJsonRef.current) {
            prevRequestJsonRef.current = data.RequestJson;
            fetchDataLocally();
        }
    }, [data?.type, data?.RequestJson, data?.url, fetchDataLocally]);

    // Initial fetch
    useEffect(() => {
        if (data?.type === "Claim" && data?.RequestJson && data?.url) {
            fetchDataLocally();
        }
        
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [data?.type, data?.RequestJson, data?.url, fetchDataLocally]);

    // If no data and not loading, show message
    if (!loading && !error && !claimData.length) {
        return (
            <div className="p-6 flex items-center justify-center text-muted-foreground bg-card rounded-lg border border-border">
                <p className="text-sm">No data available</p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
            {loading && claimData.length === 0 ? (
                // Show skeleton loaders if initially loading
                [...Array(8)].map((_, index) => (
                    <div
                        key={`skeleton-${index}`}
                        className="p-4 rounded-lg bg-card border border-border shadow-sm flex items-center justify-center h-24"
                    >
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ))
            ) : (
                // Show actual data with individual loaders
                claimData.map((item, index) => (
                    <ClaimCard
                        key={`claim-stat-${index}`}
                        label={item?.legendText}
                        value={item?.legendValue}
                        isLoading={loading}
                        error={!!error}
                    />
                ))
            )}
        </div>
    );
}

export default CompanyOverviewClaim;
